import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OpenInNewTabIcon } from "../../icons";
import { RecordLinkState, fillRecordLinkState } from "./recordLinkState";

export interface RecordLinkProps {
  /** The record route, to which `?id=` and the id are added. Not needed with `href`. */
  to?: string;
  id?: unknown;
  /** A finished link, used as it is instead of one derived from `to` and `id`. */
  href?: string;
  /** Already-rendered content, so a formatted value can also be a link. */
  text: ReactNode;
  /** "button" opens in the same tab, with a new-tab button after the link. */
  newTab?: boolean | "button";
}

function resolveId(value: unknown): string | undefined {
  if (value && typeof value === "object" && "Id" in (value as Record<string, unknown>)) {
    return String((value as { Id: unknown }).Id);
  }

  return value === null || value === undefined || value === "" ? undefined : String(value);
}

/** Links a table cell to another record's page, given a route and that record's id, or a finished href. */
export function RecordLink({ to, id, href: givenHref, text, newTab = false }: RecordLinkProps) {
  const resolvedId = resolveId(id);
  const label = text === null || text === undefined || text === "" ? "—" : text;

  const href = givenHref ?? (resolvedId ? `${to}?id=${resolvedId}` : undefined);

  if (!href) {
    return <>{label}</>;
  }

  // Filled at the press, which runs before the link navigates with it.
  const returnState: RecordLinkState = { returnTo: "" };
  const sameTabProps = {
    state: returnState,
    onClick: () => fillRecordLinkState(returnState),
  };

  if (newTab === "button") {
    return (
      <span className="d-inline-flex align-items-center gap-2">
        <Link to={href} {...sameTabProps}>
          {label}
        </Link>
        <Link
          to={href}
          target="_blank"
          rel="noopener noreferrer"
          className="link-primary link-opacity-50 link-opacity-100-hover"
          title="Open in a new tab"
          aria-label="Open in a new tab"
        >
          <FontAwesomeIcon icon={OpenInNewTabIcon} />
        </Link>
      </span>
    );
  }

  return (
    <Link
      to={href}
      target={newTab ? "_blank" : undefined}
      rel={newTab ? "noopener noreferrer" : undefined}
      {...(newTab ? {} : sameTabProps)}
    >
      {label}
    </Link>
  );
}
